import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export interface TenantConfigDto {
  id: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string | null;
  logoUrl: string | null;
  clubName: string;
  clubCity: string | null;
  clubStadium: string | null;
  clubFounded: number | null;
  appName: string | null;
  stadiumLat: number | null;
  stadiumLon: number | null;
  checkinRadiusM: number;
  checkinQrMode: string;
  subscriberDiscountPct: number;
  minAppVersion: string;
  defaultLocale: string;
  timezone: string;
  configVersion: number;
  distribution: string;
  metadata: unknown;
  updatedAt: string;
}

export interface TenantDto {
  id: string;
  slug: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  config: TenantConfigDto | null;
}

export interface CreateTenantInput {
  slug: string;
  name: string;
  primaryColor: string;
  secondaryColor: string;
  clubName: string;
  appName?: string;
  clubCity?: string;
  clubStadium?: string;
  clubFounded?: number;
}

const QK = {
  list: ['admin-tenants'] as const,
  one: (id: string) => ['admin-tenants', id] as const,
};

export function useAdminTenants() {
  return useQuery({
    queryKey: QK.list,
    queryFn: async () => {
      const res = await apiClient.get<{ data: TenantDto[] }>('/admin/tenants');
      return res.data.data;
    },
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTenantInput) => {
      const res = await apiClient.post<TenantDto>('/admin/tenants', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list }),
  });
}

export function useActivateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const url = `/admin/tenants/${id}/${active ? 'activate' : 'deactivate'}`;
      const res = await apiClient.patch<TenantDto>(url);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.list }),
  });
}
